import React, { useCallback, useEffect, useRef, useState } from 'react';
import { DefaultButton, FontIcon, getTheme, IconButton, IStyle, ITheme, mergeStyleSets, MessageBarType } from '@fluentui/react';
import { getBwjAnnouncementBannerColorScheme } from '../bwj-announcement/color';
import { IShowToastArgs } from '../../services/toast';

interface IBwjBreadSliceProps {
  content: IShowToastArgs;
  onShowFullMessage: (content: IShowToastArgs, icon: string) => void;
  onDismissMessage: (id: number) => void;
  ariaShowFullMessageLabel: string;
  ariaDismissLabel: string;
}

const MESSAGE_LENGTH_POLLING_INTERVAL = 1200; // milliseconds

const getStyleClasses = (theme: ITheme, type: MessageBarType) => {
  const colors = getBwjAnnouncementBannerColorScheme(type);

  const main: IStyle = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    fontSize: theme.fonts.smallPlus.fontSize,
    padding: 0,
    backgroundColor: theme.palette.neutralPrimary,
    width: '100%',
    height: '3.5em',
    boxSizing: 'border-box',
  };

  const iconContainer: IStyle = {
    backgroundColor: colors.main,
    height: '100%',
    display: 'flex',
    alignItems: 'center',
  };

  const icon: IStyle = {
    padding: '0.5em 0.65em',
    fontSize: '1.5em',
    color: theme.palette.white,
  };

  const messageContainer: IStyle = {
    margin: 0,
    padding: '1em',
    width: 1,
    flexGrow: 1,
    position: 'relative',
  };

  const messageBase: IStyle = {
    margin: 0,
    padding: 0,
    lineHeight: '1.5em',
    width: 'auto',
    whiteSpace: 'nowrap',
    color: theme.palette.white,
  };

  const message: IStyle = {
    ...messageBase,
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  };

  const messageRuler: IStyle = {
    ...messageBase,
    display: 'inline-block',
    position: 'absolute',
    top: -1000,
    opacity: 0,
  };

  const moreButtonContainer: IStyle = {
    padding: '0.5em',
    paddingLeft: 0,
  };

  const closeButtonContainer: IStyle = {
    padding: '0.5em',
  };

  return mergeStyleSets({
    main,
    iconContainer,
    icon,
    messageContainer,
    message,
    messageRuler,
    moreButtonContainer,
    closeButtonContainer,
  });
};

const getIconForType = (messageType: MessageBarType) => {
  switch(messageType) {
    case MessageBarType.error: return 'ErrorBadge';
    case MessageBarType.blocked: return 'BlockedSite';
    case MessageBarType.warning: return 'Warning';
    case MessageBarType.severeWarning: return 'WarningSolid';
    case MessageBarType.success: return 'SkypeCircleCheck';
    default: return 'Info';
  }
};

export const BwjBreadSlice: React.FunctionComponent<IBwjBreadSliceProps> = (props) => {
  const theme = getTheme();
  const [showDisplayFullMessageButton, setShowDisplayFullMessageButton] = useState(false);
  const { content } = props;
  const styles = getStyleClasses(theme, content.type);
  const icon = getIconForType(content.type);
  const messageContainerRef = useRef<HTMLDivElement | null>(null);
  const messageRulerRef = useRef<HTMLParagraphElement | null>(null);
  const messageRef = useRef<HTMLParagraphElement | null>(null);
  const processCancelRef = useRef<{ id: number, timeout: boolean } | undefined>(undefined);

  const startSizeMonitoring = useCallback(() => {
    const monitorSize = () => {
      const msgContainer = messageContainerRef.current;
      const msgRuler = messageRulerRef.current;
      if(msgContainer === null || msgRuler === null) { return; }

      const showBtn = msgRuler.clientWidth > msgContainer.clientWidth;
      setShowDisplayFullMessageButton(showBtn);
    }

    processCancelRef.current = { id: 0, timeout: true };
    processCancelRef.current.id = window.setTimeout(() => {
      monitorSize();
      processCancelRef.current!.timeout = false;
      processCancelRef.current!.id = window.setInterval(monitorSize, MESSAGE_LENGTH_POLLING_INTERVAL);
    }, 100);
  }, []);

  const endSizeMonitoring = useCallback(() => {
    if(processCancelRef.current === undefined) { return; }

    const process = processCancelRef.current;
    if(process.timeout) {
      window.clearTimeout(process.id);
    } else {
      window.clearInterval(process.id);
    }

    setShowDisplayFullMessageButton(false);
    processCancelRef.current = undefined;
  }, []);

  useEffect(() => {
    startSizeMonitoring();

    return () => endSizeMonitoring();
  }, [startSizeMonitoring, endSizeMonitoring]);

  const onShowFullMessage = () => {
    props.onShowFullMessage(content, icon);
  };

  return (
    <section className={styles.main}>
      <div className={styles.iconContainer}>
        <FontIcon iconName={icon} className={styles.icon} />
      </div>
      <div
        className={styles.messageContainer}
      >
        <div ref={messageContainerRef}>
          <p
            aria-hidden
            ref={messageRulerRef}
            className={styles.messageRuler}
          >
            {content.message}
          </p>
          <p
            ref={messageRef}
            className={styles.message}
          >
            {content.message}
          </p>
        </div>
      </div>
      <div
        className={styles.moreButtonContainer}
        style={{ visibility: showDisplayFullMessageButton ? 'visible' : 'hidden' }}
      >
        <DefaultButton
          styles={{
            root: {
              height: '1.5em !important',
              padding: '0.75em',
              minWidth: 1,
            },
          }}
          title={props.ariaShowFullMessageLabel}
          ariaLabel={props.ariaShowFullMessageLabel}
          iconProps={{ iconName: 'More' }}
          onClick={onShowFullMessage}
        />
      </div>
      <div className={styles.closeButtonContainer}>
        <IconButton
          ariaLabel={props.ariaDismissLabel}
          iconProps={{ iconName: 'ChromeClose' }}
          styles={{
            root: {
              height: '1.5em !important',
              color: theme.palette.white,
            },
          }}
          onClick={() => props.onDismissMessage(content.id)}
        />
      </div>
    </section>
  );
};
